import { useCallback, useEffect, useRef, useState } from 'react';
import {
  audioBlobTypeFromMime,
  pickAudioRecorderMimeType,
  pickVideoRecorderMimeType,
} from '@/lib/pitch/pitchCameraCompositor';
import { createPitchScreenCompositor } from '@/lib/pitch/pitchScreenCompositor';
import { mixPitchAudioStreams, streamHasLiveAudio } from '@/lib/pitch/mixPitchAudioStreams';
import { PITCH_RECORD_VIDEO_BITRATE } from '@/lib/pitch/pitchRecordQuality';

const STOP_FINALIZE_TIMEOUT_MS = 3000;

function liveVideoTrack(stream) {
  const track = stream?.getVideoTracks?.()[0];
  return track && track.readyState === 'live' ? track : null;
}

function stopStream(stream) {
  stream?.getTracks?.().forEach((track) => {
    track.onended = null;
    try {
      track.stop();
    } catch {
      /* already ended */
    }
  });
}

function newCaptureController() {
  try {
    return typeof window !== 'undefined' && 'CaptureController' in window
      ? new window.CaptureController()
      : null;
  } catch {
    return null;
  }
}

/**
 * Keep the user on this tab after picking a tab/window so the next click (Start recording)
 * can open the always-on-top toolbar. Chrome only honours this in the same task the
 * getDisplayMedia promise resolves in, so it must run right after the await.
 */
function keepFocusOnCapturingTab(controller, stream) {
  if (!controller?.setFocusBehavior) return;
  const surface = stream?.getVideoTracks?.()[0]?.getSettings?.().displaySurface;
  if (surface !== 'browser' && surface !== 'window') return;
  try {
    controller.setFocusBehavior('no-focus-change');
  } catch {
    /* unsupported surface or too late */
  }
}

async function requestDisplayStream({ withSystemAudio = false } = {}) {
  const controller = newCaptureController();
  const wantAudio = Boolean(withSystemAudio);
  const rich = {
    video: { frameRate: { ideal: 30 }, cursor: 'always' },
    audio: wantAudio,
    selfBrowserSurface: 'exclude',
    ...(controller ? { controller } : {}),
  };
  try {
    const stream = await navigator.mediaDevices.getDisplayMedia(rich);
    keepFocusOnCapturingTab(controller, stream);
    return stream;
  } catch (err) {
    const name = err?.name || '';
    if (name === 'NotAllowedError' || name === 'AbortError' || name === 'NotFoundError') {
      throw err;
    }
    return navigator.mediaDevices.getDisplayMedia({ video: true, audio: wantAudio });
  }
}

function friendlyShareError(err) {
  const name = err?.name || '';
  if (name === 'NotAllowedError' || name === 'AbortError') {
    return 'Screen share was cancelled';
  }
  if (name === 'NotFoundError') {
    return 'No screen or window is available to share';
  }
  return err?.message || 'Could not share the screen';
}

/**
 * Record microphone audio plus the shared screen.
 * Optional system/tab audio from getDisplayMedia is mixed with the mic.
 * When a speaker camera is on, it is captured in parallel and composited
 * into the screen recording as a bubble along the bottom.
 * The share stays live between takes until stopSharing() or the browser ends it,
 * so several modules can be recorded from one share.
 */
export function usePitchScreenRecorder({ withSpeaker = false, withSystemAudio = false } = {}) {
  const [recording, setRecording] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [speakerLoading, setSpeakerLoading] = useState(false);
  const [error, setError] = useState(null);
  const [screenStream, setScreenStream] = useState(null);
  const [speakerStream, setSpeakerStream] = useState(null);
  const [shareLostSeq, setShareLostSeq] = useState(0);
  const [systemAudioActive, setSystemAudioActive] = useState(false);

  const screenStreamRef = useRef(null);
  const speakerStreamRef = useRef(null);
  const micStreamRef = useRef(null);
  const audioMixRef = useRef(null);
  const audioStreamRef = useRef(null);
  const audioRecorderRef = useRef(null);
  const videoRecorderRef = useRef(null);
  const compositorRef = useRef(null);
  const audioChunksRef = useRef([]);
  const videoChunksRef = useRef([]);
  const audioMimeRef = useRef('');
  const startedAtRef = useRef(0);
  const recordingRef = useRef(false);
  const releasingRef = useRef(false);
  const shareHasSystemAudioRef = useRef(false);
  const withSpeakerRef = useRef(withSpeaker);
  const withSystemAudioRef = useRef(withSystemAudio);
  withSpeakerRef.current = withSpeaker;
  withSystemAudioRef.current = withSystemAudio;

  const releaseCompositor = useCallback(() => {
    compositorRef.current?.stop?.();
    compositorRef.current = null;
  }, []);

  const releaseVisual = useCallback(() => {
    releasingRef.current = true;
    releaseCompositor();
    stopStream(screenStreamRef.current);
    stopStream(speakerStreamRef.current);
    screenStreamRef.current = null;
    speakerStreamRef.current = null;
    shareHasSystemAudioRef.current = false;
    setScreenStream(null);
    setSpeakerStream(null);
    setSharing(false);
    setSpeakerLoading(false);
    setSystemAudioActive(false);
    releasingRef.current = false;
  }, [releaseCompositor]);

  const releaseAfterTake = useCallback(() => {
    releaseCompositor();
    if (!liveVideoTrack(screenStreamRef.current)) releaseVisual();
  }, [releaseCompositor, releaseVisual]);

  const releaseAudio = useCallback(() => {
    try {
      audioMixRef.current?.close?.();
    } catch {
      /* ignore */
    }
    audioMixRef.current = null;
    const mixed = audioStreamRef.current;
    if (mixed && mixed !== micStreamRef.current) {
      stopStream(mixed);
    }
    stopStream(micStreamRef.current);
    micStreamRef.current = null;
    audioStreamRef.current = null;
    audioRecorderRef.current = null;
    videoRecorderRef.current = null;
    audioMimeRef.current = '';
  }, []);

  useEffect(() => () => {
    releaseVisual();
    releaseAudio();
  }, [releaseVisual, releaseAudio]);

  const attachSpeaker = useCallback(async () => {
    setSpeakerLoading(true);
    try {
      const cam = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'user',
          width: { ideal: 640 },
          height: { ideal: 360 },
          frameRate: { ideal: 30 },
        },
        audio: false,
      });
      const camTrack = cam.getVideoTracks?.()[0];
      if (camTrack) {
        try {
          camTrack.contentHint = 'motion';
        } catch {
          /* ignore */
        }
      }
      if (!screenStreamRef.current) {
        stopStream(cam);
        return null;
      }
      stopStream(speakerStreamRef.current);
      speakerStreamRef.current = cam;
      setSpeakerStream(cam);
      return cam;
    } catch (err) {
      console.warn('Pitch speaker camera unavailable:', err);
      setError('Speaker camera unavailable — recording the shared screen only');
      return null;
    } finally {
      setSpeakerLoading(false);
    }
  }, []);

  const shareScreen = useCallback(async () => {
    setError(null);
    const wantSystemAudio = Boolean(withSystemAudioRef.current);
    const existing = liveVideoTrack(screenStreamRef.current);
    if (existing) {
      // Keep the live share. Re-opening the picker here would steal the Start-recording
      // user gesture and make the button look broken.
      if (withSpeakerRef.current && !liveVideoTrack(speakerStreamRef.current)) {
        await attachSpeaker();
      }
      if (!withSpeakerRef.current) {
        stopStream(speakerStreamRef.current);
        speakerStreamRef.current = null;
        setSpeakerStream(null);
      }
      setSharing(true);
      setSystemAudioActive(shareHasSystemAudioRef.current);
      if (wantSystemAudio && !shareHasSystemAudioRef.current) {
        setError(
          'This share has no tab audio — Stop sharing, then Share again with a Chrome Tab + “Share tab audio”. Mic will still record.',
        );
      }
      return screenStreamRef.current;
    }

    let screen;
    try {
      screen = await requestDisplayStream({ withSystemAudio: wantSystemAudio });
    } catch (err) {
      const message = friendlyShareError(err);
      setError(message);
      throw new Error(message);
    }

    const track = screen.getVideoTracks()[0];
    if (track) {
      track.onended = () => {
        if (releasingRef.current) return;
        if (recordingRef.current) {
          setShareLostSeq((n) => n + 1);
          return;
        }
        releaseVisual();
      };
    }

    const hasSystem = streamHasLiveAudio(screen);
    shareHasSystemAudioRef.current = hasSystem;
    setSystemAudioActive(hasSystem);
    if (wantSystemAudio && !hasSystem) {
      setError(
        'No system/tab audio on this share — in Chrome pick a Chrome Tab and enable “Share tab audio”. Mic will still record.',
      );
    }

    screenStreamRef.current = screen;
    setScreenStream(screen);
    setSharing(true);

    if (withSpeakerRef.current) {
      await attachSpeaker();
    }
    return screen;
  }, [attachSpeaker, releaseVisual]);

  const stopSharing = useCallback(() => {
    if (recordingRef.current) return;
    releaseVisual();
  }, [releaseVisual]);

  const start = useCallback(async () => {
    setError(null);
    audioChunksRef.current = [];
    videoChunksRef.current = [];
    try {
      await shareScreen();
      const screen = screenStreamRef.current;
      if (!liveVideoTrack(screen)) {
        throw new Error('Share a screen before recording');
      }

      const micStream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true },
      });
      micStreamRef.current = micStream;

      const systemAudioStream = streamHasLiveAudio(screen)
        ? new MediaStream(screen.getAudioTracks().filter((t) => t.readyState === 'live'))
        : null;
      const mix = mixPitchAudioStreams([micStream, systemAudioStream]);
      if (!mix.stream) {
        stopStream(micStream);
        micStreamRef.current = null;
        throw new Error('No audio captured — check your microphone');
      }
      audioMixRef.current = mix;
      audioStreamRef.current = mix.stream;

      const audioMime = pickAudioRecorderMimeType();
      audioMimeRef.current = audioMime;
      const audioRecorder = new MediaRecorder(
        mix.stream,
        audioMime ? { mimeType: audioMime } : undefined,
      );
      audioRecorder.ondataavailable = (e) => {
        if (e.data?.size) audioChunksRef.current.push(e.data);
      };
      audioRecorder.onerror = (e) => {
        console.error('Pitch screen audio recorder error:', e);
      };
      audioRecorderRef.current = audioRecorder;
      audioRecorder.start(200);

      try {
        const compositor = await createPitchScreenCompositor({
          screenStream: screen,
          speakerStream: withSpeakerRef.current ? speakerStreamRef.current : null,
        });
        compositorRef.current = compositor;
        const videoMime = pickVideoRecorderMimeType();
        const videoRecorder = new MediaRecorder(compositor.stream, {
          mimeType: videoMime,
          videoBitsPerSecond: PITCH_RECORD_VIDEO_BITRATE,
        });
        videoRecorder.ondataavailable = (e) => {
          if (e.data?.size) videoChunksRef.current.push(e.data);
        };
        videoRecorder.onerror = (e) => {
          console.error('Pitch screen video recorder error:', e);
        };
        videoRecorderRef.current = videoRecorder;
        videoRecorder.start(200);
      } catch (videoErr) {
        console.warn('Pitch screen recorder unavailable; saving audio only', videoErr);
        videoRecorderRef.current = null;
        setError(videoErr?.message || 'Screen video recorder unavailable — saving audio only');
      }

      startedAtRef.current = Date.now();
      recordingRef.current = true;
      setRecording(true);
    } catch (err) {
      recordingRef.current = false;
      releaseAudio();
      const message = err?.message || 'Could not start screen recording';
      setError(message);
      throw new Error(message);
    }
  }, [releaseAudio, shareScreen]);

  const stop = useCallback(() => {
    return new Promise((resolve, reject) => {
      const audioRec = audioRecorderRef.current;
      const videoRec = videoRecorderRef.current;
      if (!audioRec) {
        reject(new Error('Not recording'));
        return;
      }

      let audioDone = false;
      let videoDone = !videoRec;
      let settled = false;

      const finalize = () => {
        if (settled) return;
        if (!audioDone || !videoDone) return;
        settled = true;
        clearTimeout(timeoutId);

        recordingRef.current = false;
        const durationSec = Math.max(1, (Date.now() - startedAtRef.current) / 1000);
        const mime = audioRec.mimeType || audioMimeRef.current || pickAudioRecorderMimeType();
        const audioBlob = new Blob(audioChunksRef.current, {
          type: audioBlobTypeFromMime(mime),
        });
        const videoBlob = videoChunksRef.current.length
          ? new Blob(videoChunksRef.current, { type: 'video/webm' })
          : null;

        releaseAudio();
        releaseAfterTake();
        setRecording(false);

        if (audioBlob.size < 64) {
          reject(new Error('No audio captured — check your microphone and try again'));
          return;
        }
        resolve({ audioBlob, videoBlob, durationSec, audioMime: mime });
      };

      const timeoutId = setTimeout(() => {
        console.warn('Pitch screen recorder stop timed out; finalizing with captured audio');
        videoDone = true;
        if (!audioDone && audioRec.state === 'recording') {
          try {
            audioRec.requestData();
            audioRec.stop();
          } catch {
            audioDone = true;
            finalize();
          }
          return;
        }
        audioDone = true;
        finalize();
      }, STOP_FINALIZE_TIMEOUT_MS);

      audioRec.onstop = () => {
        audioDone = true;
        finalize();
      };
      if (videoRec) {
        videoRec.onstop = () => {
          videoDone = true;
          finalize();
        };
      }

      try {
        if (audioRec.state === 'recording') {
          audioRec.requestData();
          audioRec.stop();
        } else {
          audioDone = true;
        }
        if (videoRec?.state === 'recording') {
          videoRec.requestData();
          videoRec.stop();
        } else {
          videoDone = true;
        }
        finalize();
      } catch (err) {
        settled = true;
        clearTimeout(timeoutId);
        recordingRef.current = false;
        releaseAudio();
        releaseAfterTake();
        setRecording(false);
        reject(err);
      }
    });
  }, [releaseAudio, releaseAfterTake]);

  return {
    recording,
    sharing,
    speakerLoading,
    error,
    screenStream,
    speakerStream,
    shareLostSeq,
    systemAudioActive,
    shareScreen,
    stopSharing,
    start,
    stop,
  };
}
